import { EntityId } from "@reduxjs/toolkit";

import { IconButton, Popover, Box, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/PrivacyTip";
import CloudOffIcon from "@mui/icons-material/CloudOff";

import {
    usePopupState,
    bindTrigger,
    bindPopover,
} from "material-ui-popup-state/hooks";

import { RootState } from "app/store";
import { useAppSelector } from "app/hooks";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { selectSiteById } from "features/sites/store/selectors";

import { SiteEventsContent } from "./SiteEventsContent";
import { SiteEvent } from "types/SiteEvent";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { MatchType } from "features/appFeatures/types/matchType";
import { SiteInformationContent } from "./SiteInformationContent";
import SiteSchedule from "types/SiteSchedule";

export interface SiteInformationCardProps {
    siteId: EntityId;
    title: string;
    siteEvents: SiteEvent[];
    siteSchedule: SiteSchedule;
}

export function SiteInformationCard(props: SiteInformationCardProps) {
    const popupState = usePopupState({
        variant: "popover",
        popupId: "siteInformationCard",
    });

    const isSensitiveSiteDetailsFeatureEnabled = useAppSelector(
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(
                state,
                [AppFeature.LiveStreamingSensitiveSiteDetails],
                MatchType.All,
            ),
    );

    const site = useAppSelector((state) =>
        selectSiteById(state, props.siteId.toString()),
    );

    const isAllSiteInformationAvailable =
        site.siteInformation && site.openingInformation && site.primaryAddress;
    const isAnySiteInformationAvailable =
        site.siteInformation || site.openingInformation || site.primaryAddress;

    return (
        <>
            <IconButton
                title="club information"
                {...bindTrigger(popupState)}
                disabled={!isAnySiteInformationAvailable}
                color="alwaysWhite"
            >
                <InfoIcon />
            </IconButton>

            <Popover
                {...bindPopover(popupState)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                sx={{ maxWidth: "100%" }}
            >
                <Box
                    sx={{
                        padding: "4px",
                        flexGrow: 1,
                        borderRadius: "2px",
                        bgcolor: (theme) => theme.palette.primary.main,
                        margin: "4px",
                    }}
                >
                    <Typography
                        variant="h6"
                        color="alwaysWhite"
                        sx={{ paddingLeft: "8px" }}
                    >
                        {props.title}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        padding: "10px",
                    }}
                >
                    {site.siteInformation && (
                        <Box width={"50rem"}>
                            <SiteInformationContent
                                site={site}
                                siteSchedule={props.siteSchedule}
                                showSensitiveData={
                                    isSensitiveSiteDetailsFeatureEnabled
                                }
                            />
                            {isSensitiveSiteDetailsFeatureEnabled && (
                                <SiteEventsContent
                                    siteEvents={props.siteEvents}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </Popover>

            {!isAllSiteInformationAvailable && (
                <IconButton
                    disableRipple
                    sx={{ cursor: "default" }}
                    color="alwaysWhite"
                >
                    <CloudOffIcon titleAccess="Not all club information available" />
                </IconButton>
            )}
        </>
    );
}
